import type { HighchartsOptionsWithSemiosAdditions } from 'components/StackedChart/types'
import type { Chart, Options } from 'highcharts'
import type { MouseEventHandler, TouchEventHandler } from 'react'
import { useEffect, useRef } from 'react'
import { Highcharts } from './_utils/highcharts-init'

type HighchartsChartProps = {
  id?: string
  className?: string
  onChartCreated?: (chart: Chart) => void
  onMouseMove?: MouseEventHandler
  onMouseLeave?: MouseEventHandler
  onTouchMove?: TouchEventHandler
} & (
  | { constructorType?: 'stockChart'; chartOptions: HighchartsOptionsWithSemiosAdditions }
  | { constructorType: 'chart'; chartOptions: Options }
)

export const HighchartsChart = ({
  chartOptions,
  constructorType,
  onChartCreated,
  id,
  className,
  onMouseMove,
  onMouseLeave,
  onTouchMove,
}: HighchartsChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstanceRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!chartRef.current) return

    if (!chartInstanceRef.current) {
      Highcharts.setOptions({
        lang: {
          thousandsSep: ',',
        },
      })

      chartInstanceRef.current =
        constructorType === 'chart'
          ? Highcharts.chart(chartRef.current, chartOptions)
          : Highcharts.stockChart(chartRef.current, chartOptions)

      onChartCreated?.(chartInstanceRef.current)
    } else if (chartOptions) {
      chartInstanceRef.current.update(chartOptions, true, true)
    }
  }, [chartOptions, chartRef.current, chartInstanceRef.current])

  useEffect(() => {
    // tear down/clean up
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()

        chartInstanceRef.current = null
      }
    }
  }, [])

  return (
    <div
      className={className}
      css={{ width: '100%' }}
      id={id}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onTouchMove={onTouchMove}
      ref={chartRef}
    />
  )
}
